import type * as React from "react";

import { ApiError } from "@/api/ApiResult";
import { useApportionmentStateRequest } from "@/hooks/apportionment/useApportionmentStateRequest";
import type { ApportionmentState } from "@/types/generated/openapi";
import { ApportionmentProviderContext } from "../hooks/ApportionmentProviderContext";
import { useApportionmentRequest } from "../hooks/useApportionmentRequest";

export interface ElectionApportionmentProviderProps {
  children: React.ReactNode;
  electionId: number;
}

export function ApportionmentProvider({ children, electionId }: ElectionApportionmentProviderProps) {
  const { error: apportionmentError, data, refetch: refetchApportionment } = useApportionmentRequest(electionId);
  const { requestState, refetch: refetchState } = useApportionmentStateRequest(electionId);

  // Refetch both the apportionment state and result
  async function refetch(controller?: AbortController) {
    await Promise.all([refetchApportionment(), refetchState(controller)]);
  }

  let error = apportionmentError;
  let state: ApportionmentState | undefined;
  if (requestState.status === "success") {
    state = requestState.data;
  } else if (requestState.status === "api-error") {
    error = requestState.error;
  }
  if (error && !(error instanceof ApiError)) {
    throw error;
  }

  return (
    <ApportionmentProviderContext.Provider
      value={{
        seatAssignment: data?.seat_assignment,
        candidateNomination: data?.candidate_nomination,
        electionSummary: data?.election_totals,
        warnings: data?.warnings ?? [],
        state,
        error,
        isLoading: requestState.status === "loading",
        refetch,
      }}
    >
      {children}
    </ApportionmentProviderContext.Provider>
  );
}
